// Same-origin API + WebSocket by default so the backend origin is NEVER baked into
// the client bundle (inspecting the shipped JS won't reveal it) and the app follows
// whatever public domain served it — a cleaner fronted domain works with no rebuild.
// In production the origin's reverse proxy path-routes `/api` and `/ws` on that single
// domain. The Vite envs override for local dev, where the web (5173) and the backends
// (3001/2567) are different origins and must be addressed absolutely.
function sameOriginHttp(path: string): string {
  if (typeof window !== "undefined") return `${window.location.origin}${path}`;
  return "http://localhost:3001";
}
function sameOriginWs(path: string): string {
  if (typeof window !== "undefined") {
    const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${wsProto}//${window.location.host}${path}`;
  }
  return "ws://localhost:2567";
}

// `||` (not `??`) so an EMPTY build arg — how prod ships, to keep the origin out of the
// bundle — falls through to the same-origin derivation.
export const API_URL: string = import.meta.env.VITE_API_URL || sameOriginHttp("/api");
export const GAME_SERVER_URL: string = import.meta.env.VITE_GAME_SERVER_URL || sameOriginWs("/ws");

/**
 * A lobby tile's PRESENTATION (name, glyph, player count). Deliberately carries NO
 * activation flag: the lobby's truth is the server (`GET /games` → ACTIVE | COMING_SOON,
 * admin-togglable live), and the PRE-LOAD fallback comes from the shared
 * `DEFAULT_GAME_STATUSES` — the very map the api's boot-seed and the game-server's gate
 * read. There used to be a second, hand-maintained `enabled` boolean here, and it had
 * drifted: شلم/پاسور/بلوف were `true` while the defaults (and prod) said COMING_SOON, and
 * منچ was `false` while it was live — so for the first moments after launch the lobby
 * showed the wrong state and a tap could be refused by the server.
 */
export interface GameInfo {
  id: string;
  fa: string;
  en: string;
  /** Player count, shown in Persian digits. */
  players: string;
  icon: string;
}

/** Games with an online multiplayer room wired up — every game in the catalogue
 * qualifies (all are multi-human). Shared by the lobby (which games get an
 * "آنلاین" badge) and the deep-link router. */
export const ONLINE_GAME_IDS: ReadonlySet<string> = new Set([
  "hearts",
  "hokm",
  "shelem",
  "pasur",
  "bluff",
  "chess",
  "backgammon",
  "mensch",
  "dooz",
]);

/** The lobby's game catalogue — presentation only. Which tiles are PLAYABLE comes from
 *  `GET /games` (and, before it lands, from the shared `DEFAULT_GAME_STATUSES`). */
export const GAMES: readonly GameInfo[] = [
  { id: "hearts", fa: "بی‌دل", en: "Hearts", players: "۴", icon: "♥️" },
  { id: "hokm", fa: "حکم", en: "Hokm", players: "۴", icon: "👑" },
  /*
   * ⚠️ 💰, NOT 🃏 — AND THE GLYPH IT GAVE UP IS حکم'S (operator, 2026-09-21). شلم carried the
   * playing-card mark while حکم's START SCREEN wears the same one (`BADGE_EXCEPTIONS` in
   * `game-stats.test.tsx`: its lobby tile is the حاکم's crown, its table the card it is played
   * with) — so one glyph stood for two games, the same fault as تخته نرد's die on منچ, one step
   * behind. 💰 is not invented here: the bot's own شلم tutorial (`apps/bot/src/tutorials.ts`) has
   * used it all along, and شلم IS the bidding game — the contract is a price.
   */
  { id: "shelem", fa: "شلم", en: "Shelem", players: "۴", icon: "💰" },
  // «۲ یا ۴»: a table for TWO (one against one) or for FOUR (two opposite partnerships) — each
  // size its own room since ps-pasur-2p4p (operator, 2026-10-20). Both sizes, NEVER a range:
  // «۲–۴» (بلوف's form) would promise a table for three that does not exist (the coordinator's
  // ruling, 2026-10-20 — منچ, which plays two to four, keeps its «۴»).
  { id: "pasur", fa: "پاسور", en: "Pasur", players: "۲ یا ۴", icon: "🎣" },
  { id: "bluff", fa: "بلوف", en: "Bluff", players: "۳–۶", icon: "🎭" },
  { id: "chess", fa: "شطرنج", en: "Chess", players: "۲", icon: "♟️" },
  { id: "backgammon", fa: "تخته نرد", en: "Backgammon", players: "۲", icon: "🎲" },
  /*
   * ⚠️ **منچ USED TO CARRY تخته نرد'S DIE, AND THIS IS THE ONE MAP THE WHOLE APP READS.** The
   * lobby tile, the per-game breakdown, the match-history row, the invite sheet and the start
   * screen's badge all resolve a game's mark HERE, so two games sharing one glyph said, on every
   * one of those surfaces, that they are the same game — and «برترین‌ها»'s filter chips, where a
   * mark IS the identifier, could not tell them apart at all. Both games roll a die; the die is
   * تخته نرد's, whose whole board is played by it. منچ is a RACE — the app's own invite line for
   * it is «ببینم کی زودتر مهره‌هاشو می‌بره خونه» — so it takes the finish.
   * ⚠️ منچ had no distinct mark ANYWHERE to inherit: this is a choice, not a copy. Changing it is
   * one line here and every surface follows.
   */
  { id: "mensch", fa: "منچ", en: "Ludo", players: "۴", icon: "🏁" },
  { id: "dooz", fa: "دوز", en: "Connect Four", players: "۲", icon: "🔴" },
];
